/* eslint-disable react/jsx-no-useless-fragment */
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import {
  BLOCKS,
  Block,
  Document,
  INLINES,
  Inline,
  MARKS,
} from '@contentful/rich-text-types';
import NextLink from 'next/link';
import React, { ReactNode } from 'react';

import { Image } from '@sola/ark-skippy/atoms/image/image.js';
import { Video } from '@sola/ark-skippy/atoms/video/video.js';
import { Card } from '@sola/ark-skippy/molecules/card/card.js';
import { BasicContentMain } from '@sola/ark-skippy/organisms-cms/basic-content/main/basic-content-main.js';
import { GalleryCinema } from '@sola/ark-skippy/organisms-cms/gallery/cinema/gallery-cinema.js';
import { keys } from '@sola/contentful-model/keys.js';
import { getArticlePath } from '@sola/contentful-model/lib/article.js';
import { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.js';
import {
  ContentResponseBody,
  ContentResponseDto,
} from '@sola/schema/lib/contentful.js';
import { FlexibleContentProps } from '@sola/schema/lib/schema.js';
import { decapitalize, trailingSlash } from '@sola/utils/string.ts';
import {
  isAbsoluteInternalLink,
  isAnchorLink,
  isInternalLink,
  isMailToLink,
  isTelLink,
} from '@sola/utils/url.js';

import {
  convertContentResponseToFlexibleContent,
  convertMediaResponseToMedia,
} from './response';

const handleArrayChildren = (children: React.ReactNode, keyPrefix: string) => {
  return React.Children.map(children, (child, index) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        key: child.key || `${keyPrefix}-child-${index}`,
      });
    }
    return child;
  });
};

const Bold = ({ children }: { children: React.ReactNode }) => (
  <span className="bold">{handleArrayChildren(children, 'bold')}</span>
);

const Italic = ({ children }: { children: React.ReactNode }) => (
  <span className="italic">{handleArrayChildren(children, 'italic')}</span>
);

const SoftReturn = () => <br />;

export const getRichText = (
  body: ContentResponseBody | null | undefined,
  articleType?: string,
) => {
  if (!body) {
    return undefined;
  }

  const embedCounter = {
    embed: 0,
    media: 0,
    update: 0,
  };

  const tableOfContents: Array<{
    id: string;
    title: string;
    subtitle?: string;
  }> = [];

  const options = {
    preserveWhitespace: true,
    renderText: (text: React.ReactNode) => {
      const str = text?.toString() ?? '';
      const lines = str.split('\n');
      return (
        <>
          {lines.map((line, index) => (
            <React.Fragment key={`line-${index}-${line}`}>
              {line}
              {index < lines.length - 1 && <SoftReturn key={index} />}
            </React.Fragment>
          ))}
        </>
      );
    },
    renderMark: {
      [MARKS.BOLD]: (text: React.ReactNode) => <Bold>{text}</Bold>,
      [MARKS.ITALIC]: (text: React.ReactNode) => <Italic>{text}</Italic>,
    },
    renderNode: {
      [INLINES.ENTRY_HYPERLINK]: (
        node: Block | Inline,
        children: React.ReactNode,
      ) => {
        const { data } = node;
        const targetId = (data as { target?: { sys: { id: string } } })?.target
          ?.sys?.id;
        const link = body?.links?.entries?.hyperlink?.find?.(
          (item) => item?.sys?.id === targetId,
        );
        const isDownload =
          decapitalize(link?.__typename) === keys.contentTypes.downloads;

        let path = link?.path;

        if (!path && link?.slug && link?.taxonomyArticleType) {
          path = trailingSlash(
            getArticlePath(
              link.slug,
              link.taxonomyArticleType as ArticleTypesEnum,
            ),
          );
        }

        if (isDownload) {
          path = `/file/${targetId}`;
        }

        return (
          <NextLink
            target={isDownload ? '_blank' : undefined}
            href={path || '#'}
          >
            {children}
          </NextLink>
        );
      },
      [INLINES.HYPERLINK]: (
        node: Block | Inline,
        children: React.ReactNode,
      ) => {
        const { data } = node;
        const uri = (data as { uri?: string })?.uri || '';
        const isInternal = isInternalLink(uri) || isAbsoluteInternalLink(uri);
        const isAnchor = isAnchorLink(uri);
        const isMailto = isMailToLink(uri);
        const isTel = isTelLink(uri);

        // Handle special link types
        if (isAnchor) {
          return <a href={uri}>{children}</a>;
        }

        if (isMailto) {
          const mailtoUri = uri.includes('mailto:') ? uri : `mailto:${uri}`;
          return <a href={mailtoUri}>{children}</a>;
        }

        if (isTel) {
          const telUri = uri.includes('tel:') ? uri : `tel:${uri}`;
          return <a href={telUri}>{children}</a>;
        }

        // Handle internal and external links
        const target = isInternal ? '_self' : '_blank';
        const rel = isInternal ? '' : 'noopener noreferrer';

        return (
          <NextLink target={target} href={uri}>
            {children}
          </NextLink>
        );
      },
      [BLOCKS.HEADING_2]: (node: Block | Inline, children: React.ReactNode) => {
        return <h2 className="heading-lg">{children}</h2>;
      },
      [BLOCKS.HEADING_3]: (node: Block | Inline, children: React.ReactNode) => {
        return <h3 className="heading-md">{children}</h3>;
      },
      [BLOCKS.HEADING_4]: (node: Block | Inline, children: React.ReactNode) => {
        return <h4 className="heading-sm">{children}</h4>;
      },
      [BLOCKS.PARAGRAPH]: (node: Block | Inline, children: React.ReactNode) => {
        return <p>{children}</p>;
      },
      [BLOCKS.LIST_ITEM]: (node: Block | Inline, children: React.ReactNode) => {
        return <li>{children}</li>;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: Block | Inline) => {
        const { data } = node;
        const target = (data as { target?: { sys: { id: string } } })?.target;
        const link = Array.isArray(body?.links?.entries?.block)
          ? body?.links?.entries?.block.find(
              (item: { sys: { id?: string } }) =>
                item?.sys?.id === target?.sys.id,
            )
          : undefined;

        if (!link || !link.__typename) {
          return <>Content not found</>;
        }

        if (link.__typename === 'ImageWrapper') {
          embedCounter.media += 1;
          const convertedMedia = convertMediaResponseToMedia(link);
          const id = `media-${embedCounter.media}`;
          return (
            <div className="Richtext__embed" id={id}>
              {!!convertedMedia && (
                <>
                  {convertedMedia.video?.url && (
                    <Video
                      {...convertedMedia.video}
                      url={convertedMedia.video.url}
                    />
                  )}
                  {convertedMedia?.image && !convertedMedia.video?.url && (
                    <Image
                      image={convertedMedia?.image}
                      aspectRatio="16-9"
                      rounded="lg"
                    />
                  )}
                  {convertedMedia.caption && (
                    <div className="Richtext__embed-attribution label label-lg mt-comfy-sm">
                      {convertedMedia.caption}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        }

        if (link.__typename === 'FlexibleContent') {
          let renderBody;
          if (link.body) {
            renderBody = link.body.json
              ? getRichText(link.body)?.content
              : documentToReactComponents(link.body as unknown as Document);
          }
          const convertedContent =
            convertContentResponseToFlexibleContent(link);

          if (
            (link.items && link.items.length) ||
            (link.itemsCollection?.items && link.itemsCollection.items.length)
          ) {
            embedCounter.embed += 1;
            const id = `embed-${embedCounter.embed}`;
            tableOfContents.push({
              id,
              title: convertedContent?.title || `Gallery ${embedCounter.embed}`,
            });
            const items: FlexibleContentProps[] = link.items
              ? link.items
              : link.itemsCollection.items?.map((item: ContentResponseDto) => {
                  let renderBodyItem;
                  if (item.body) {
                    renderBodyItem = item.body.json
                      ? getRichText(item.body)?.content
                      : documentToReactComponents(
                          item.body as unknown as Document,
                        );
                  }
                  const convertedItem =
                    convertContentResponseToFlexibleContent(item);

                  return {
                    ...convertedItem,
                    body: renderBodyItem,
                    media: convertedItem?.media,
                  };
                }) || [];

            return (
              <div className="Richtext__embed" id={id}>
                <GalleryCinema
                  {...convertedContent}
                  media={undefined}
                  body={renderBody as ReactNode}
                  items={items}
                  alt
                  inline
                />
              </div>
            );
          }

          if (articleType === keys.articleTypes.crisisUpdate) {
            embedCounter.update += 1;
            const id = `update-${embedCounter.update}`;
            tableOfContents.push({
              id,
              title: convertedContent?.title || `Update ${embedCounter.update}`,
              subtitle: convertedContent?.displayDate,
            });
            return (
              <div className="Richtext__embed" id={id}>
                <Card
                  {...convertedContent}
                  body={renderBody as ReactNode}
                  variant="article"
                />
              </div>
            );
          }

          embedCounter.embed += 1;
          const id = `section-${embedCounter.embed}`;
          tableOfContents.push({
            id,
            title: convertedContent?.title || `Section ${embedCounter.embed}`,
          });
          return (
            <div className="Richtext__embed" id={id}>
              <BasicContentMain
                {...convertedContent}
                body={renderBody as ReactNode}
                alt
                inline
              />
            </div>
          );
        }

        return <>Invalid embedded entry</>;
      },
    },
  };

  const content = documentToReactComponents(
    body?.json as unknown as Document,
    options,
  ) as ReactNode;

  return {
    content,
    tableOfContents,
  };
};
